import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkLogin, logoutUser } from '../redux/slices/authSlice';
import { searchTourPackagesByName, clearSearchResults } from '../redux/slices/packageSlice';
import LoginPopup from './Login';
import SearchResults from '../Pages/SearchResult';

const Header = ({ page = 'home' }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSidebarSearch, setIsSidebarSearch] = useState(false);
    const timeoutRef = useRef(null);
    const searchTimeoutRef = useRef(null);
    const dispatch = useDispatch();
    const { isLoggedIn, user, loading } = useSelector((state) => state.auth);
    const destinations = ["Thailand", "Kashi", "Shirdi", "Kerala", "Kanchi", "Destinations", "Goa", "Tamil Nadu"];

    const [placeholder, setPlaceholder] = useState("");
    const [index, setIndex] = useState(0); // Current destination index
    const [subIndex, setSubIndex] = useState(0); // Current letter index
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        const currentWord = destinations[index];

        // Typing & erasing speed
        const typingSpeed = deleting ? 50 : 120;

        const timeout = setTimeout(() => {
            if (!deleting && subIndex < currentWord.length) {
                // Typing forward
                setSubIndex((prev) => prev + 1);
                setPlaceholder(currentWord.substring(0, subIndex + 1));
            } else if (deleting && subIndex > 0) {
                // Erasing backward
                setSubIndex((prev) => prev - 1);
                setPlaceholder(currentWord.substring(0, subIndex - 1));
            } else if (!deleting && subIndex === currentWord.length) {
                // Pause before deleting
                setTimeout(() => setDeleting(true), 1000);
            } else if (deleting && subIndex === 0) {
                // Move to next word after erasing
                setDeleting(false);
                setIndex((prev) => (prev + 1) % destinations.length);
            }
        }, typingSpeed);

        return () => clearTimeout(timeout);
    }, [subIndex, deleting, index]);


    const handleSearch = (value, isSidebar = false) => {
        console.log('handleSearch called with value:', value, 'isSidebar:', isSidebar);
        setSearchTerm(value);
        setIsSidebarSearch(isSidebar);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (value.trim().length > 2) {
            console.log('Search term length > 2, scheduling API call for:', value);
            searchTimeoutRef.current = setTimeout(() => {
                console.log('Dispatching search with term:', value);
                dispatch(searchTourPackagesByName(value));
            }, 500);
        } else {
            console.log('Clearing search results for term:', value);
            dispatch(clearSearchResults());
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log('Form submission prevented');
    };

    useEffect(() => {
        if (!isSidebarOpen && searchTerm && isSidebarSearch) {
            console.log('Sidebar closed, clearing sidebar search term:', searchTerm);
            setSearchTerm('');
            setIsSidebarSearch(false);
            dispatch(clearSearchResults());
        }
    }, [isSidebarOpen, searchTerm, isSidebarSearch, dispatch]);

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const handleLogout = () => {
        dispatch(logoutUser());
        setIsDropdownOpen(false);
    };

    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setIsDropdownOpen(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setIsDropdownOpen(false);
        }, 500);
    };

    const isViewMorePage = page === 'viewmore';
    const headerBgClass = isViewMorePage ? '' : 'bg-custom-white shadow-sm';
    const borderClass = isViewMorePage ? 'border-none' : 'border-custom-yellow';
    const textColorClass = isViewMorePage ? 'text-white' : 'text-custom-blue';
    const iconColorClass = isViewMorePage ? 'text-white' : 'text-gray-400';
    const rounded = isViewMorePage ? 'rounded-none' : 'rounded-xl';
    const logoSrc = isViewMorePage ? '/pplogowhite.png' : '/pplogocut.png';

    return (
        <>
            <header className={`p-4  flex ${rounded} justify-between items-center border-b ${headerBgClass} ${borderClass} ${isViewMorePage ? 'bg-white/1 backdrop-blur-[1px]' : 'bg-white/70 backdrop-blur-md shadow-sm'} relative z-30`}>
                <a href="/" className="flex items-center gap-2">
                    <img src={logoSrc} alt="Logo" className="w-8 h-8 sm:w-14 sm:h-14 object-contain" />
                    <span className={`text-lg sm:text-xl font-bold ${textColorClass}`}>
                        Prapancha Pravasa
                    </span>
                </a>

                <div className="hidden md:flex items-center space-x-4">
                    <form onSubmit={handleSubmit} className="relative">
                        <input
                            type="text"
                            placeholder={`Search for ${placeholder}`}
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                handleSearch(e.target.value, false);
                            }}
                            className={`border-2 rounded-full py-2 px-4 pl-10 w-64 focus:outline-none focus:ring-2 placeholder:text-gray-900 ${isViewMorePage
                                ? "border-white/50 bg-white/20 text-white placeholder:text-white/90 focus:ring-white"
                                : "border-gray-200 focus:ring-custom-blue text-gray-800 placeholder:text-gray-900"
                                }`}
                        />
                        <svg
                            className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${iconColorClass}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                        </svg>
                    </form>
                    {isLoggedIn ? (
                        <div
                            className="relative"
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        >
                            <div className="flex items-center space-x-2 cursor-pointer">
                                <div className="w-8 h-8 bg-custom-yellow text-white rounded-full flex items-center justify-center">
                                    {user?.name?.charAt(0) || 'G'}
                                </div>
                                <span className={`text-sm ${textColorClass}`}>
                                    Hi, {user?.name || 'User'}
                                </span>
                            </div>
                            {isDropdownOpen && (
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border z-50">
                                    <div className="flex flex-col">
                                        <a
                                            href="/user/profile"
                                            className="flex items-center px-4 py-2 text-custom-blue bg-custom-yellow"
                                        >
                                            <span className="mr-2">
                                                <img src="/user-avatar.png" alt="User Avatar" className="w-5 h-5" />
                                            </span>
                                            My Profile
                                        </a>
                                        {/* <a
                                            href="/user/profile"
                                            className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100"
                                        >
                                            <span className="mr-2">
                                                <img src="/event.png" alt="User Avatar" className="w-5 h-5" />
                                            </span>
                                            My Bookings
                                        </a> */}
                                        <a
                                            href="/about-us"
                                            className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100"
                                        >
                                            <span className="mr-2">
                                                <img src="/question.png" alt="User Avatar" className="w-5 h-5" />
                                            </span>
                                            Contact Us
                                        </a>
                                        <button
                                            onClick={handleLogout}
                                            className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 text-left"
                                        >
                                            <span className="mr-2">
                                                <img src="/logout.png" alt="User Avatar" className="w-5 h-5" />
                                            </span>
                                            Logout
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <button
                            className={`text-sm font-semibold px-4 ${textColorClass}`}
                            onClick={() => setIsPopupOpen(true)}
                        >
                            Login
                        </button>
                    )}
                </div>

                <button className={`md:hidden ${textColorClass} focus:outline-none`} onClick={toggleSidebar}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d={isSidebarOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
                        />
                    </svg>
                </button>
            </header>

            {/* Search Results Overlay */}
            {searchTerm && searchTerm.length > 2 && (
                <div className="fixed top-20 left-0 right-0 h-[100vh] bg-white/95 z-30 overflow-y-auto">
                    <div className="max-w-7xl mx-auto px-4 py-6">
                        <SearchResults searchTerm={searchTerm} />
                    </div>
                </div>
            )}

            <div
                className={`fixed top-0 right-0 h-full w-64 bg-custom-white shadow-lg transform transition-transform duration-300 ease-in-out z-30 ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full'}`}
            >
                <div className="p-4">
                    <button className="absolute top-4 right-4 text-custom-blue" onClick={toggleSidebar}>
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    <div className="mt-12 flex flex-col space-y-4">
                        {isLoggedIn ? (
                            <div className="flex items-center space-x-2">
                                <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center">
                                    {user?.name?.charAt(0) || 'G'}
                                </div>
                                <span className="text-sm text-custom-blue">Hi, {user?.name || 'User'}</span>
                            </div>
                        ) : null}
                        <form onSubmit={handleSubmit} className="relative">
                            <input
                                type="text"
                                placeholder="Search for tours..."
                                value={searchTerm}
                                onChange={(e) => {
                                    console.log('Sidebar input onChange, value:', e.target.value);
                                    handleSearch(e.target.value, true);
                                }}
                                className="border border-gray-300 rounded-full py-2 px-4 pl-10 w-full focus:outline-none focus:ring-2 focus:ring-custom-blue"
                            />
                            <svg
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </form>
                        {/* <div className="flex items-center space-x-2">
                            <img src="https://via.placeholder.com/20?text=Flag" alt="Flag" className="h-5 w-5" />
                            <span className="text-sm text-custom-blue">INR ₹</span>
                            <svg className="w-4 h-4 text-custom-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </div> */}
                        {isLoggedIn ? (
                            <div className="flex flex-col space-y-4">
                                <a href="/user/profile" className="text-sm text-custom-blue hover:underline">
                                    My Profile
                                </a>
                                <a href="/user/profile" className="text-sm text-custom-blue hover:underline">
                                    My Bookings
                                </a>
                                <a href="#" className="text-sm text-custom-blue hover:underline">
                                    Support & FAQs
                                </a>
                                <button
                                    onClick={handleLogout}
                                    className="text-sm text-custom-blue hover:underline text-left"
                                >
                                    Logout
                                </button>
                            </div>
                        ) : (
                            <button
                                className="text-sm text-custom-blue hover:underline"
                                onClick={() => {
                                    setIsPopupOpen(true);
                                    setIsSidebarOpen(false);
                                }}
                            >
                                Login
                            </button>
                        )}
                    </div>
                </div>
            </div>
            <LoginPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />

            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                    onClick={toggleSidebar}
                ></div>
            )}
        </>
    );
};

export default Header;